
import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Copy, Eye, EyeOff, RefreshCw, Bug, Save } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

interface AdasAnalysisDebuggerProps {
  isLoading: boolean;
  requestData?: {
    estimateText: string;
    adasFeatures: any;
    vectorStoreId: string;
  };
  responseData?: any;
  onRetry?: () => void;
}

export function AdasAnalysisDebugger({
  isLoading,
  requestData,
  responseData,
  onRetry,
}: AdasAnalysisDebuggerProps) {
  const [showFullText, setShowFullText] = useState(false);
  
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const formatJson = (data: any) => {
    return JSON.stringify(data, null, 2);
  };

  const truncateText = (text: string, maxLength = 500) => {
    if (!text) return "";
    return showFullText ? text : text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Extract features from ADAS data
  const extractAdasFeatures = (adasFeatures: any) => {
    if (!adasFeatures) return [];
    
    // This is a simplified approach - in a real implementation, 
    // you'd want to properly parse the structure based on what ChromeData returns
    try {
      if (typeof adasFeatures === 'object') {
        return Object.entries(adasFeatures).map(([key, value]) => ({
          name: key,
          value: JSON.stringify(value)
        }));
      }
      return [];
    } catch (e) {
      console.error("Error parsing ADAS features:", e);
      return [];
    }
  };

  // Extract file search results
  const extractFileSearchResults = (response: any) => {
    try {
      if (!response?.output) return [];
      
      const fileSearchCall = response.output.find((item: any) => item.type === "file_search_call");
      return fileSearchCall?.search_results || [];
    } catch (e) {
      console.error("Error extracting file search results:", e);
      return [];
    }
  };

  // Extract text response
  const extractTextResponse = (response: any) => {
    try {
      if (!response?.output) return "";
      
      const message = response.output.find((item: any) => item.type === "message");
      if (!message) return "";
      
      const textContent = message.content?.find((item: any) => item.type === "output_text");
      return textContent?.text || "";
    } catch (e) {
      console.error("Error extracting response text:", e);
      return "";
    }
  };

  // Extract full prompt from debug data
  const extractFullPrompt = (response: any) => {
    try {
      return response?.debug?.fullPrompt || "Full prompt not available";
    } catch (e) {
      console.error("Error extracting full prompt:", e);
      return "Error extracting full prompt";
    }
  };

  // Extract system prompt from debug data
  const extractSystemPrompt = (response: any) => {
    try {
      return response?.debug?.systemPrompt || "System prompt not available";
    } catch (e) {
      console.error("Error extracting system prompt:", e);
      return "Error extracting system prompt";
    }
  };

  const adasFeaturesList = requestData?.adasFeatures ? extractAdasFeatures(requestData.adasFeatures) : [];
  const fileSearchResults = responseData ? extractFileSearchResults(responseData) : [];
  const textResponse = responseData ? extractTextResponse(responseData) : "";
  const fullPrompt = responseData ? extractFullPrompt(responseData) : "";
  const systemPrompt = responseData ? extractSystemPrompt(responseData) : "";

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <Bug className="mr-2 h-5 w-5" />
              ADAS Analysis Debugger
            </CardTitle>
            <CardDescription>
              Inspect request and response data for the ADAS analysis
            </CardDescription>
          </div>
          <div className="flex space-x-2">
            {onRetry && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onRetry}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Retry
              </Button>
            )}
            <Button
              variant="outline" 
              size="sm"
              onClick={() => setShowFullText(!showFullText)}
            >
              {showFullText ? (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Truncate Text
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Show Full Text
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="request">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="request">Request</TabsTrigger>
            <TabsTrigger value="prompt">Full Prompt</TabsTrigger>
            <TabsTrigger value="response">Response</TabsTrigger>
            <TabsTrigger value="adas-features">ADAS Features</TabsTrigger>
          </TabsList>
          
          <TabsContent value="request">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Request Details</CardTitle>
              </CardHeader>
              <CardContent>
                {requestData ? (
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium">Vector Store ID</h3>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(requestData.vectorStoreId, "Vector Store ID")}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <pre className="bg-muted p-2 rounded-md text-xs font-mono">
                        {requestData.vectorStoreId}
                      </pre>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium">Estimate Text</h3>
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline">
                            {requestData.estimateText?.length || 0} chars
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(requestData.estimateText, "Estimate Text")}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <ScrollArea className="h-60 w-full">
                        <pre className="bg-muted p-2 rounded-md text-xs font-mono whitespace-pre-wrap">
                          {truncateText(requestData.estimateText)}
                        </pre>
                      </ScrollArea>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium">ADAS Features</h3>
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline">
                            {requestData.adasFeatures ? Object.keys(requestData.adasFeatures).length : 0} features
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(JSON.stringify(requestData.adasFeatures, null, 2), "ADAS Features")}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <ScrollArea className="h-40 w-full">
                        <pre className="bg-muted p-2 rounded-md text-xs font-mono">
                          {requestData.adasFeatures ? formatJson(requestData.adasFeatures) : "No ADAS features provided"}
                        </pre>
                      </ScrollArea>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    No request data available
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="prompt">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Full Prompt Details</CardTitle>
              </CardHeader>
              <CardContent>
                {responseData ? (
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium">System Prompt</h3>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(systemPrompt, "System Prompt")}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <ScrollArea className="h-60 w-full">
                        <pre className="bg-muted p-2 rounded-md text-xs font-mono whitespace-pre-wrap">
                          {systemPrompt}
                        </pre>
                      </ScrollArea>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium">Full User Prompt</h3>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(fullPrompt, "Full Prompt")}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <ScrollArea className="h-60 w-full">
                        <pre className="bg-muted p-2 rounded-md text-xs font-mono whitespace-pre-wrap">
                          {fullPrompt}
                        </pre>
                      </ScrollArea>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    No prompt data available
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="response">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Response Details</CardTitle>
              </CardHeader>
              <CardContent className="max-h-[600px] overflow-y-auto">
                {isLoading ? (
                  <div className="py-8 flex flex-col items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin mb-4" />
                    <p className="text-muted-foreground">Waiting for ADAS analysis response...</p>
                  </div>
                ) : responseData ? (
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium">Text Response</h3>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(textResponse, "Text Response")}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <ScrollArea className="h-60 w-full">
                        <div className="bg-muted p-4 rounded-md text-sm whitespace-pre-wrap">
                          {textResponse || "No text response available"}
                        </div>
                      </ScrollArea>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium">Raw Response</h3>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(JSON.stringify(responseData, null, 2), "Raw Response")}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <ScrollArea className="h-60 w-full">
                        <pre className="bg-muted p-2 rounded-md text-xs font-mono">
                          {formatJson(responseData)}
                        </pre>
                      </ScrollArea>
                    </div>
                    
                    {fileSearchResults.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-medium">File Search Results</h3>
                          <Badge variant="outline">
                            {fileSearchResults.length} results
                          </Badge>
                        </div>
                        <ScrollArea className="h-60 w-full">
                          <div className="space-y-4">
                            {fileSearchResults.map((result: any, index: number) => (
                              <div key={index} className="bg-muted p-2 rounded-md">
                                <div className="flex justify-between items-center mb-2">
                                  <Badge variant="outline" className="bg-blue-50 text-blue-800">
                                    {result.filename || "Unknown file"}
                                  </Badge>
                                  <Badge variant="outline">
                                    Score: {(result.score * 100).toFixed(2)}%
                                  </Badge>
                                </div>
                                <div className="text-xs font-mono whitespace-pre-wrap">
                                  {result.text || "No text content"}
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    No response data available
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="adas-features">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">ADAS Features Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                {adasFeaturesList.length > 0 ? (
                  <ScrollArea className="h-[500px]">
                    <div className="grid grid-cols-1 gap-2">
                      {adasFeaturesList.map((feature, index) => (
                        <div key={index} className="p-2 bg-muted rounded-md">
                          <div className="font-medium text-sm">{feature.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">{feature.value}</div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    No ADAS features detected in the data
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="flex justify-between">
        <div className="text-xs text-muted-foreground">
          {responseData ? (
            <>
              Request ID: {responseData.id || 'Unknown'} | 
              Model: {responseData.model || 'Unknown'} | 
              Tokens: {responseData.usage?.total_tokens || 'N/A'}
            </>
          ) : (
            'No response data available'
          )}
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => {
            if (responseData) {
              const debugData = {
                timestamp: new Date().toISOString(),
                request: requestData,
                response: responseData
              };
              localStorage.setItem('adas_debug_' + Date.now(), JSON.stringify(debugData));
              toast.success('Debug data saved to local storage');
            }
          }} disabled={!responseData}>
            <Save className="h-4 w-4 mr-2" />
            Save Debug Data
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
